<template>
  <div class="container">
    <!-- Page Header with Machine Selection -->
    <div class="page-header-bar">
      <div class="box-title">
        <h4>
          <i class="fal fa-code-branch"></i>
          ຕັ້ງຄ່າເສັ້ນທາງພິເສດ
          <span class="countTotal" v-if="totalCount">({{ totalCount }})</span>
        </h4>
      </div>
      <!-- Machine Selection - Compact Bar -->
      <div class="machine-select-bar">
        <div class="machine-select-group">
          <label class="select-label">
            <i class="fal fa-server"></i> ເຄື່ອງ:
          </label>
          <model-list-select
            :list="machineOptions"
            v-model="machineCode"
            placeholder="ເລືອກເຄື່ອງຄັດແຍກ..."
            option-text="display_name"
            option-value="machine_code"
            @input="onMachineChanged"
            class="machine-select"
          />
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-section" v-if="statistics">
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-icon total">
            <i class="fal fa-route"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{
              statistics.by_route ? statistics.by_route.length : 0
            }}</span>
            <span class="stat-label">ເສັ້ນທາງ</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon branches">
            <i class="fal fa-layer-group"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ statistics.total || 0 }}</span>
            <span class="stat-label">ສາຂາທັງໝົດ</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon active">
            <i class="fal fa-check-circle"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ statistics.active || 0 }}</span>
            <span class="stat-label">ເປີດໃຊ້ງານ</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon inactive">
            <i class="fal fa-times-circle"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ statistics.inactive || 0 }}</span>
            <span class="stat-label">ປິດໃຊ້ງານ</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Box -->
    <div class="box">
      <div class="section-header">
        <h5 class="section-title">
          <i class="fal fa-filter"></i>
          ຄົ້ນຫາ ແລະ ກັ່ນຕອງ
        </h5>
        <div class="header-actions">
          <vs-button @click="openAddDialog">
            <i class="fal fa-plus" style="margin-right: 5px"></i>
            ເພີ່ມສາຂາ
          </vs-button>
          <vs-button success @click="openBulkAddDialog">
            <i class="fal fa-plus-square" style="margin-right: 5px"></i>
            ເພີ່ມຫຼາຍສາຂາ
          </vs-button>
        </div>
      </div>
      <div class="filters-row">
        <div class="filter-group">
          <label class="filter-label">ຄົ້ນຫາ:</label>
          <div class="search-input-wrapper">
            <i class="fal fa-search search-icon"></i>
            <input
              type="text"
              v-model="filters.search"
              @keyup.enter="onSearch"
              placeholder="ຊື່ສາຂາ, ລະຫັດ..."
              class="search-input"
            />
          </div>
        </div>
        <div class="filter-group">
          <label class="filter-label">ກຸ່ມເສັ້ນທາງ:</label>
          <model-list-select
            :list="availableRoutes"
            v-model="filters.route_name"
            placeholder="ທັງໝົດ"
            option-text="display_name"
            option-value="route_name"
            class="filter-select"
          />
        </div>
        <div class="filter-group">
          <label class="filter-label">ສະຖານະ:</label>
          <select v-model="filters.is_active" class="status-select">
            <option value="">ທັງໝົດ</option>
            <option value="1">ເປີດໃຊ້ງານ</option>
            <option value="0">ປິດໃຊ້ງານ</option>
          </select>
        </div>
        <div class="filter-actions">
          <vs-button flat @click="onSearch" :loading="isLoading">
            <i class="fal fa-search" style="margin-right: 5px"></i>
            ຄົ້ນຫາ
          </vs-button>
          <vs-button flat warn @click="resetFilters">
            <i class="fal fa-undo" style="margin-right: 5px"></i>
            ລ້າງ
          </vs-button>
        </div>
      </div>
    </div>

    <!-- Route Summary Cards -->
    <div
      class="box"
      v-if="statistics && statistics.by_route && statistics.by_route.length > 0"
    >
      <div class="section-header">
        <h5 class="section-title">
          <i class="fal fa-chart-pie"></i>
          ສະຫຼຸບກຸ່ມເສັ້ນທາງ
        </h5>
      </div>
      <div class="route-summary-cards">
        <div
          class="route-summary-card"
          v-for="route in statistics.by_route"
          :key="route.route_name"
          @click="filterByRoute(route.route_name)"
          :class="{ active: filters.route_name === route.route_name }"
        >
          <div class="route-icon">
            <i class="fal fa-route"></i>
          </div>
          <div class="route-info">
            <span class="route-name">{{ route.route_name }}</span>
            <span class="route-stats">
              Chute {{ route.chute_number }} | {{ route.count }} ສາຂາ
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table Box -->
    <div class="box">
      <div class="section-header">
        <h5 class="section-title">
          <i class="fal fa-list"></i>
          ລາຍການສາຂາໃນເສັ້ນທາງ
        </h5>
      </div>

      <!-- Loading -->
      <Loading v-if="isLoading" />

      <!-- Data Table -->
      <div class="table-container" v-else>
        <table class="data-table" v-if="routeBranches.length > 0">
          <thead>
            <tr>
              <th class="col-no">ລຳດັບ</th>
              <th class="col-branch">ສາຂາ</th>
              <th class="col-location">ທີ່ຕັ້ງສາຂາ</th>
              <th class="col-route">ກຸ່ມເສັ້ນທາງ</th>
              <th class="col-machine">ເຄື່ອງຄັດແຍກ</th>
              <th class="col-status">ສະຖານະ</th>
              <th class="col-created">ວັນທີສ້າງ</th>
              <th class="col-actions">ຈັດການ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in routeBranches" :key="item.id">
              <td class="col-no">
                {{ (currentPage - 1) * perPage + index + 1 }}
              </td>
              <td class="col-branch">
                <div class="branch-info">
                  <span class="branch-name">{{ item.branch?.name }}</span>
                  <span class="branch-code">{{
                    item.branch?.prefix || item.branch?.code
                  }}</span>
                </div>
              </td>
              <td class="col-location">
                <div class="location-info" v-if="item.branch">
                  <span class="province-name">
                    ແຂວງ
                    {{ item.branch?.village?.district?.province?.name || "-" }}
                  </span>
                  <span
                    class="district-name"
                    v-if="item.branch?.village?.district"
                  >
                    {{ item.branch.village.district.name }}
                  </span>
                </div>
                <span v-else>-</span>
              </td>
              <td class="col-route">
                <span class="route-badge">
                  <i class="fal fa-route"></i>
                  {{ item.route_name }}
                </span>
              </td>
              <td class="col-machine">
                <span v-if="item.machine_code" class="machine-badge">
                  <i class="fal fa-cogs"></i>
                  {{ item.machine_code }}
                </span>
                <span v-else class="global-badge">
                  <i class="fal fa-globe"></i> ທົ່ວໄປ
                </span>
              </td>
              <td class="col-status">
                <span
                  :class="[
                    'status-badge',
                    item.is_active ? 'active' : 'inactive',
                  ]"
                >
                  {{ item.is_active ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ" }}
                </span>
              </td>
              <td class="col-created">
                <span class="created-info" v-if="item.created_at">
                  {{ formatDate(item.created_at) }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="col-actions">
                <div class="action-buttons">
                  <vs-button
                    flat
                    size="small"
                    @click="toggleStatus(item)"
                    :title="item.is_active ? 'ປິດໃຊ້ງານ' : 'ເປີດໃຊ້ງານ'"
                  >
                    <i
                      :class="
                        item.is_active
                          ? 'fal fa-toggle-on'
                          : 'fal fa-toggle-off'
                      "
                    ></i>
                  </vs-button>
                  <vs-button
                    flat
                    danger
                    size="small"
                    @click="confirmDelete(item)"
                    title="ລຶບ"
                  >
                    <i class="fal fa-trash"></i>
                  </vs-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div class="empty-state" v-else>
          <i class="fal fa-inbox"></i>
          <p>ບໍ່ພົບຂໍ້ມູນ</p>
        </div>

        <!-- Pagination -->
        <div class="pagination-container" v-if="totalPages > 1">
          <vs-pagination
            v-model="currentPage"
            :length="totalPages"
            @input="onPageChange"
          />
        </div>
      </div>
    </div>

    <!-- Add Single Branch Dialog -->
    <vs-dialog v-model="showAddDialog" width="600px" prevent-close>
      <template #header>
        <h4 class="dialog-title">
          <i class="fal fa-plus"></i>
          ເພີ່ມສາຂາເຂົ້າກຸ່ມເສັ້ນທາງ
        </h4>
      </template>

      <div class="dialog-content">
        <!-- Machine Selection -->
        <div class="form-row">
          <label class="form-label">ເຄື່ອງຄັດແຍກ:</label>
          <div class="form-control">
            <model-list-select
              :list="machineOptions"
              v-model="form.machine_code"
              placeholder="ເລືອກເຄື່ອງຄັດແຍກ (ຫຼື ປະໄວ້ເປັນທົ່ວໄປ)"
              option-text="display_name"
              option-value="machine_code"
              @input="onFormMachineChange"
            />
            <span class="form-hint">ປະໄວ້ຫວ່າງ ຖ້າຕ້ອງການໃຊ້ກັບທຸກເຄື່ອງ</span>
          </div>
        </div>

        <!-- Route Selection -->
        <div class="form-row">
          <label class="form-label required">ກຸ່ມເສັ້ນທາງ:</label>
          <div class="form-control">
            <model-list-select
              :list="formAvailableRoutes"
              v-model="form.route_name"
              placeholder="ເລືອກກຸ່ມເສັ້ນທາງ..."
              option-text="display_name"
              option-value="route_name"
              :isDisabled="isLoadingRoutes"
            />
            <span class="text-danger" v-if="formErrors.route_name">
              {{ formErrors.route_name }}
            </span>
            <span class="form-hint" v-if="isLoadingRoutes">
              <i class="fal fa-spinner fa-spin"></i> ກຳລັງໂຫຼດ...
            </span>
          </div>
        </div>

        <!-- Branch Selection -->
        <div class="form-row">
          <label class="form-label required">ສາຂາ:</label>
          <div class="form-control">
            <model-list-select
              :list="availableBranches"
              v-model="form.branch_id"
              placeholder="ເລືອກສາຂາ..."
              option-text="display_name"
              option-value="id"
              :isDisabled="isLoadingBranches"
            />
            <span class="text-danger" v-if="formErrors.branch_id">
              {{ formErrors.branch_id }}
            </span>
            <span class="form-hint" v-if="isLoadingBranches">
              <i class="fal fa-spinner fa-spin"></i> ກຳລັງໂຫຼດສາຂາ...
            </span>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <vs-button flat @click="closeAddDialog">ຍົກເລີກ</vs-button>
          <vs-button @click="submitAddForm" :loading="isSubmitting">
            <i class="fal fa-save" style="margin-right: 5px"></i>
            ເພີ່ມ
          </vs-button>
        </div>
      </template>
    </vs-dialog>

    <!-- Bulk Add Dialog -->
    <vs-dialog v-model="showBulkAddDialog" width="700px" prevent-close>
      <template #header>
        <h4 class="dialog-title">
          <i class="fal fa-plus-square"></i>
          ເພີ່ມຫຼາຍສາຂາເຂົ້າກຸ່ມເສັ້ນທາງ
        </h4>
      </template>

      <div class="dialog-content">
        <!-- Machine Selection -->
        <div class="form-row">
          <label class="form-label">ເຄື່ອງຄັດແຍກ:</label>
          <div class="form-control">
            <model-list-select
              :list="machineOptions"
              v-model="bulkForm.machine_code"
              placeholder="ເລືອກເຄື່ອງຄັດແຍກ (ຫຼື ປະໄວ້ເປັນທົ່ວໄປ)"
              option-text="display_name"
              option-value="machine_code"
              @input="onBulkFormMachineChange"
            />
            <span class="form-hint">ປະໄວ້ຫວ່າງ ຖ້າຕ້ອງການໃຊ້ກັບທຸກເຄື່ອງ</span>
          </div>
        </div>

        <!-- Route Selection -->
        <div class="form-row">
          <label class="form-label required">ກຸ່ມເສັ້ນທາງ:</label>
          <div class="form-control">
            <model-list-select
              :list="bulkFormAvailableRoutes"
              v-model="bulkForm.route_name"
              placeholder="ເລືອກກຸ່ມເສັ້ນທາງ..."
              option-text="display_name"
              option-value="route_name"
              :isDisabled="isLoadingRoutes"
            />
            <span class="text-danger" v-if="bulkFormErrors.route_name">
              {{ bulkFormErrors.route_name }}
            </span>
          </div>
        </div>

        <!-- Branch Search -->
        <div class="form-row">
          <label class="form-label">ຄົ້ນຫາສາຂາ:</label>
          <div class="form-control">
            <div class="search-input-wrapper">
              <i class="fal fa-search search-icon"></i>
              <input
                type="text"
                v-model="bulkBranchSearch"
                @input="onBulkBranchSearchChange"
                placeholder="ຊື່ສາຂາ, ລະຫັດ..."
                class="search-input full-width"
              />
            </div>
          </div>
        </div>

        <!-- Available Branches List -->
        <div class="form-row">
          <label class="form-label required">ເລືອກສາຂາ:</label>
          <div class="form-control">
            <div class="branch-selection-list" v-if="!isLoadingBranches">
              <div
                v-for="branch in availableBranches"
                :key="branch.id"
                class="branch-selection-item"
                :class="{ selected: bulkForm.branch_ids.includes(branch.id) }"
                @click="toggleBranchSelection(branch.id)"
              >
                <div class="branch-checkbox">
                  <i
                    :class="
                      bulkForm.branch_ids.includes(branch.id)
                        ? 'fas fa-check-square'
                        : 'far fa-square'
                    "
                  ></i>
                </div>
                <div class="branch-details">
                  <span class="branch-name">{{ branch.name }}</span>
                  <span class="branch-code">{{
                    branch.code || branch.prefix
                  }}</span>
                  <span
                    class="branch-location"
                    v-if="branch.district_name || branch.province_prefix"
                  >
                    - ເມືອງ {{ branch.district_name
                    }}<span
                      v-if="branch.district_name && branch.province_prefix"
                      >, </span
                    >{{ branch.province_prefix }}
                  </span>
                </div>
              </div>
              <div class="empty-branches" v-if="availableBranches.length === 0">
                <i class="fal fa-inbox"></i>
                <span>ບໍ່ພົບສາຂາທີ່ສາມາດເພີ່ມໄດ້</span>
              </div>
            </div>
            <div class="loading-branches" v-else>
              <i class="fal fa-spinner fa-spin"></i> ກຳລັງໂຫຼດສາຂາ...
            </div>
            <span class="text-danger" v-if="bulkFormErrors.branch_ids">
              {{ bulkFormErrors.branch_ids }}
            </span>
            <span class="form-hint" v-if="bulkForm.branch_ids.length > 0">
              ເລືອກແລ້ວ {{ bulkForm.branch_ids.length }} ສາຂາ
            </span>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <vs-button flat @click="closeBulkAddDialog">ຍົກເລີກ</vs-button>
          <vs-button @click="submitBulkAddForm" :loading="isSubmitting">
            <i class="fal fa-save" style="margin-right: 5px"></i>
            ເພີ່ມ {{ bulkForm.branch_ids.length }} ສາຂາ
          </vs-button>
        </div>
      </template>
    </vs-dialog>

    <!-- Delete Confirmation Dialog -->
    <vs-dialog v-model="showDeleteDialog" width="400px">
      <template #header>
        <h4 class="dialog-title danger">
          <i class="fal fa-exclamation-triangle"></i>
          ຢືນຢັນການລຶບ
        </h4>
      </template>

      <div class="dialog-content">
        <p v-if="deletingItem">
          ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບສາຂາ
          <strong>{{ deletingItem.branch?.name }}</strong>
          ອອກຈາກກຸ່ມເສັ້ນທາງ <strong>{{ deletingItem.route_name }}</strong
          >?
        </p>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <vs-button flat @click="showDeleteDialog = false">ຍົກເລີກ</vs-button>
          <vs-button danger @click="deleteRouteBranch" :loading="isDeleting">
            <i class="fal fa-trash" style="margin-right: 5px"></i>
            ລຶບ
          </vs-button>
        </div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import Loading from "@/views/Loading";
import { ModelListSelect } from "vue-search-select";

export default {
  name: "RouteBranchManagement",

  components: {
    Loading,
    ModelListSelect,
  },

  data() {
    return {
      // Machine Selection
      machineCode: null,
      machineOptions: [],

      // Data
      routeBranches: [],
      statistics: null,
      machines: [],
      availableRoutes: [],
      availableBranches: [],

      // Loading states
      isLoading: false,
      isLoadingRoutes: false,
      isLoadingBranches: false,
      isSubmitting: false,
      isDeleting: false,

      // Pagination
      currentPage: 1,
      perPage: 15,
      totalPages: 1,
      totalCount: 0,

      // Filters
      filters: {
        search: "",
        route_name: null,
        is_active: "",
      },

      // Add Dialog
      showAddDialog: false,
      form: {
        branch_id: null,
        route_name: null,
        machine_code: null,
      },
      formErrors: {},
      formAvailableRoutes: [],

      // Bulk Add Dialog
      showBulkAddDialog: false,
      bulkForm: {
        branch_ids: [],
        route_name: null,
        machine_code: null,
      },
      bulkFormErrors: {},
      bulkFormAvailableRoutes: [],
      bulkBranchSearch: "",
      bulkBranchSearchTimeout: null,

      // Delete dialog
      showDeleteDialog: false,
      deletingItem: null,
    };
  },

  created() {
    this.fetchInitialData();
  },

  methods: {
    async fetchInitialData() {
      await this.fetchMachines();
      await Promise.all([this.fetchAllRoutes(), this.fetchStatistics()]);
      this.fetchRouteBranches();
    },

    async fetchMachines() {
      try {
        const response = await this.$http.get("sorting/admin/machines", {
          params: {
            is_active: true,
            find_by_user: true,
          },
        });
        if (response.data && response.data.success) {
          this.machines = response.data.data || [];
          // Create display names for machine options
          this.machineOptions = this.machines.map((machine) => ({
            ...machine,
            display_name: `${machine.machine_code} - ${machine.distribution_center_name}`,
          }));
          // Auto-select first machine if available
          if (this.machines.length > 0) {
            this.machineCode = this.machines[0].machine_code;
            await this.fetchAvailableRoutes(this.machines[0].machine_code);
          }
        } else {
          this.machines = [];
          this.machineOptions = [];
        }
      } catch (error) {
        console.error("Failed to fetch machines:", error);
        this.machines = [];
      }
    },

    async fetchAllRoutes() {
      try {
        const params = {};
        if (this.machineCode) {
          params.machine_code = this.machineCode;
        }
        const response = await this.$http.get(
          "sorting/admin/route-branches/all-routes",
          { params }
        );
        if (response.data && response.data.success) {
          this.availableRoutes = (response.data.data || []).map((route) => ({
            ...route,
            display_name: `${route.route_name} (Chute ${route.chute_number})`,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch all routes:", error);
      }
    },

    async fetchAvailableRoutes(machineCode) {
      this.isLoadingRoutes = true;
      try {
        const params = {};
        if (machineCode) {
          params.machine_code = machineCode;
        }
        const response = await this.$http.get(
          "sorting/admin/route-branches/available-routes",
          { params }
        );
        if (response.data && response.data.success) {
          const routes = (response.data.data || []).map((route) => ({
            ...route,
            display_name: `${route.route_name} (Chute ${route.chute_number}, ${route.branch_count} ສາຂາ)`,
          }));
          return routes;
        }
        return [];
      } catch (error) {
        console.error("Failed to fetch available routes:", error);
        return [];
      } finally {
        this.isLoadingRoutes = false;
      }
    },

    async fetchStatistics() {
      try {
        const params = {};
        if (this.machineCode) {
          params.machine_code = this.machineCode;
        }
        if (this.filters.route_name) {
          params.route_name = this.filters.route_name;
        }
        const response = await this.$http.get(
          "sorting/admin/route-branches/statistics",
          { params }
        );
        if (response.data && response.data.success) {
          this.statistics = response.data.data;
        }
      } catch (error) {
        console.error("Failed to fetch statistics:", error);
      }
    },

    async fetchRouteBranches() {
      this.isLoading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
        };

        if (this.filters.search) {
          params.search = this.filters.search;
        }
        if (this.machineCode) {
          params.machine_code = this.machineCode;
        }
        if (this.filters.route_name) {
          params.route_name = this.filters.route_name;
        }
        if (this.filters.is_active !== "") {
          params.is_active = this.filters.is_active;
        }

        const response = await this.$http.get("sorting/admin/route-branches", {
          params,
        });

        if (response.data && response.data.success) {
          this.routeBranches = response.data.data || [];
          if (response.data.pagination) {
            this.totalCount = response.data.pagination.total || 0;
            this.totalPages = response.data.pagination.last_page || 1;
            this.currentPage = response.data.pagination.current_page || 1;
          }
        }
      } catch (error) {
        console.error("Failed to fetch route branches:", error);
        this.$vs.notification({
          color: "danger",
          title: "ເກີດຂໍ້ຜິດພາດ",
          text: "ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchAvailableBranches(search = "") {
      this.isLoadingBranches = true;
      try {
        const params = {
          limit: 100,
        };
        if (search) {
          params.search = search;
        }
        if (this.form.machine_code || this.bulkForm.machine_code) {
          params.machine_code =
            this.form.machine_code || this.bulkForm.machine_code;
        }

        const response = await this.$http.get(
          "sorting/admin/route-branches/available-branches",
          { params }
        );

        if (response.data && response.data.success) {
          this.availableBranches = (response.data.data || []).map((b) => {
            const branchCode = b.code || b.prefix || "";
            const branchName = b.name || "";
            const districtName = `ເມືອງ ${b.district?.name || ""}`;
            const provinceName = b.province?.name || "";
            const provincePrefix = `${
              b.province?.prefix || ""
            } ${provinceName}`;

            let displayParts = [];
            if (branchCode) displayParts.push(`[${branchCode}]`);
            displayParts.push(branchName);

            let locationParts = [];
            if (districtName) locationParts.push(districtName);
            if (provincePrefix) locationParts.push(provincePrefix);
            if (provinceName) locationParts.push(provinceName);
            if (locationParts.length > 0) {
              displayParts.push(`- ${locationParts.join(", ")}`);
            }

            return {
              ...b,
              display_name: displayParts.join(" "),
              district_name: districtName,
              province_name: provinceName,
              province_prefix: provincePrefix,
            };
          });
        }
      } catch (error) {
        console.error("Failed to fetch available branches:", error);
      } finally {
        this.isLoadingBranches = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("lo-LA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    resetFilters() {
      this.filters = {
        search: "",
        route_name: null,
        is_active: "",
      };
      this.currentPage = 1;
      this.fetchRouteBranches();
      this.fetchStatistics();
    },

    async onMachineChanged() {
      this.filters.route_name = null;
      this.availableRoutes = await this.fetchAvailableRoutes(this.machineCode);
      this.currentPage = 1;
      await this.fetchRouteBranches();
      await this.fetchStatistics();
    },

    filterByRoute(routeName) {
      if (this.filters.route_name === routeName) {
        this.filters.route_name = null;
      } else {
        this.filters.route_name = routeName;
      }
      this.onSearch();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchRouteBranches();
    },

    onSearch() {
      this.currentPage = 1;
      this.fetchRouteBranches();
      this.fetchStatistics();
    },

    // Add Dialog
    async openAddDialog() {
      this.form = {
        branch_id: null,
        route_name: null,
        machine_code: this.machineCode || null,
      };
      this.formErrors = {};
      this.availableBranches = [];
      this.showAddDialog = true;

      // Fetch routes for the form
      this.formAvailableRoutes = await this.fetchAvailableRoutes(
        this.form.machine_code
      );
      await this.fetchAvailableBranches();
    },

    closeAddDialog() {
      this.showAddDialog = false;
      this.formErrors = {};
    },

    async onFormMachineChange() {
      this.form.route_name = null;
      this.form.branch_id = null;
      this.formAvailableRoutes = await this.fetchAvailableRoutes(
        this.form.machine_code
      );
      this.fetchAvailableBranches();
    },

    validateAddForm() {
      this.formErrors = {};

      if (!this.form.route_name) {
        this.formErrors.route_name = "ກະລຸນາເລືອກກຸ່ມເສັ້ນທາງ";
      }

      if (!this.form.branch_id) {
        this.formErrors.branch_id = "ກະລຸນາເລືອກສາຂາ";
      }

      return Object.keys(this.formErrors).length === 0;
    },

    async submitAddForm() {
      if (!this.validateAddForm()) return;

      this.isSubmitting = true;
      try {
        const response = await this.$http.post("sorting/admin/route-branches", {
          branch_id: this.form.branch_id,
          route_name: this.form.route_name,
          machine_code: this.form.machine_code,
        });

        if (response.data && response.data.success) {
          this.$vs.notification({
            color: "success",
            title: "ສຳເລັດ",
            text: "ເພີ່ມສາຂາເຂົ້າກຸ່ມເສັ້ນທາງສຳເລັດ",
          });
          this.closeAddDialog();
          this.fetchRouteBranches();
          this.fetchStatistics();
        } else {
          this.$vs.notification({
            color: "danger",
            title: "ເກີດຂໍ້ຜິດພາດ",
            text: response.data?.message || "ບໍ່ສາມາດເພີ່ມສາຂາໄດ້",
          });
        }
      } catch (error) {
        console.error("Submit error:", error);
        const message = error.response?.data?.message || "ບໍ່ສາມາດເພີ່ມສາຂາໄດ້";
        this.$vs.notification({
          color: "danger",
          title: "ເກີດຂໍ້ຜິດພາດ",
          text: message,
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    // Bulk Add Dialog
    async openBulkAddDialog() {
      this.bulkForm = {
        branch_ids: [],
        route_name: null,
        machine_code: this.machineCode || null,
      };
      this.bulkFormErrors = {};
      this.bulkBranchSearch = "";
      this.availableBranches = [];
      this.showBulkAddDialog = true;

      // Fetch routes for the form
      this.bulkFormAvailableRoutes = await this.fetchAvailableRoutes(
        this.bulkForm.machine_code
      );
      this.fetchAvailableBranches();
    },

    closeBulkAddDialog() {
      this.showBulkAddDialog = false;
      this.bulkFormErrors = {};
    },

    async onBulkFormMachineChange() {
      this.bulkForm.route_name = null;
      this.bulkForm.branch_ids = [];
      this.bulkFormAvailableRoutes = await this.fetchAvailableRoutes(
        this.bulkForm.machine_code
      );
      this.fetchAvailableBranches();
    },

    onBulkBranchSearchChange() {
      clearTimeout(this.bulkBranchSearchTimeout);
      this.bulkBranchSearchTimeout = setTimeout(() => {
        this.fetchAvailableBranches(this.bulkBranchSearch);
      }, 300);
    },

    toggleBranchSelection(branchId) {
      const index = this.bulkForm.branch_ids.indexOf(branchId);
      if (index > -1) {
        this.bulkForm.branch_ids.splice(index, 1);
      } else {
        this.bulkForm.branch_ids.push(branchId);
      }
    },

    validateBulkAddForm() {
      this.bulkFormErrors = {};

      if (!this.bulkForm.route_name) {
        this.bulkFormErrors.route_name = "ກະລຸນາເລືອກກຸ່ມເສັ້ນທາງ";
      }

      if (this.bulkForm.branch_ids.length === 0) {
        this.bulkFormErrors.branch_ids = "ກະລຸນາເລືອກສາຂາຢ່າງໜ້ອຍ 1 ສາຂາ";
      }

      return Object.keys(this.bulkFormErrors).length === 0;
    },

    async submitBulkAddForm() {
      if (!this.validateBulkAddForm()) return;

      this.isSubmitting = true;
      try {
        const response = await this.$http.post(
          "sorting/admin/route-branches/bulk",
          {
            branch_ids: this.bulkForm.branch_ids,
            route_name: this.bulkForm.route_name,
            machine_code: this.bulkForm.machine_code,
          }
        );

        if (response.data && response.data.success) {
          const { added, skipped } = response.data.data || {};
          let message = `ເພີ່ມ ${added} ສາຂາສຳເລັດ`;
          if (skipped > 0) {
            message += `, ຂ້າມ ${skipped} ສາຂາທີ່ມີແລ້ວ`;
          }
          this.$vs.notification({
            color: "success",
            title: "ສຳເລັດ",
            text: message,
          });
          this.closeBulkAddDialog();
          this.fetchRouteBranches();
          this.fetchStatistics();
        } else {
          this.$vs.notification({
            color: "danger",
            title: "ເກີດຂໍ້ຜິດພາດ",
            text: response.data?.message || "ບໍ່ສາມາດເພີ່ມສາຂາໄດ້",
          });
        }
      } catch (error) {
        console.error("Submit error:", error);
        const message = error.response?.data?.message || "ບໍ່ສາມາດເພີ່ມສາຂາໄດ້";
        this.$vs.notification({
          color: "danger",
          title: "ເກີດຂໍ້ຜິດພາດ",
          text: message,
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    // Toggle Status
    async toggleStatus(item) {
      try {
        const response = await this.$http.put(
          `sorting/admin/route-branches/${item.id}`,
          {
            is_active: !item.is_active,
          }
        );

        if (response.data && response.data.success) {
          this.$vs.notification({
            color: "success",
            title: "ສຳເລັດ",
            text: `${item.is_active ? "ປິດ" : "ເປີດ"}ໃຊ້ງານສຳເລັດ`,
          });
          this.fetchRouteBranches();
          this.fetchStatistics();
        } else {
          this.$vs.notification({
            color: "danger",
            title: "ເກີດຂໍ້ຜິດພາດ",
            text: response.data?.message || "ບໍ່ສາມາດອັບເດດສະຖານະໄດ້",
          });
        }
      } catch (error) {
        console.error("Toggle status error:", error);
        const message =
          error.response?.data?.message || "ບໍ່ສາມາດອັບເດດສະຖານະໄດ້";
        this.$vs.notification({
          color: "danger",
          title: "ເກີດຂໍ້ຜິດພາດ",
          text: message,
        });
      }
    },

    // Delete
    confirmDelete(item) {
      this.deletingItem = item;
      this.showDeleteDialog = true;
    },

    async deleteRouteBranch() {
      if (!this.deletingItem) return;

      this.isDeleting = true;
      try {
        const response = await this.$http.delete(
          `sorting/admin/route-branches/${this.deletingItem.id}`
        );

        if (response.data && response.data.success) {
          this.$vs.notification({
            color: "success",
            title: "ສຳເລັດ",
            text: "ລຶບສາຂາອອກຈາກກຸ່ມເສັ້ນທາງສຳເລັດ",
          });
          this.showDeleteDialog = false;
          this.deletingItem = null;
          this.fetchRouteBranches();
          this.fetchStatistics();
        } else {
          this.$vs.notification({
            color: "danger",
            title: "ເກີດຂໍ້ຜິດພາດ",
            text: response.data?.message || "ບໍ່ສາມາດລຶບຂໍ້ມູນໄດ້",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        const message = error.response?.data?.message || "ບໍ່ສາມາດລຶບຂໍ້ມູນໄດ້";
        this.$vs.notification({
          color: "danger",
          title: "ເກີດຂໍ້ຜິດພາດ",
          text: message,
        });
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Page Header with Machine Selection
.page-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 10px;

  .box-title {
    margin-bottom: 0;

    h4 {
      margin-bottom: 0;
      font-size: 18px;
      align-items: baseline;

      i {
        margin-right: 8px;
        color: #195bff;
      }

      .countTotal {
        font-size: 14px;
        color: #666;
        font-weight: normal;
      }
    }
  }
}

// Machine Selection Bar
.machine-select-bar {
  display: flex;
  align-items: center;
  gap: 10px;

  .machine-select-group {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f8f9fa;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;

    .select-label {
      font-size: 12px;
      font-weight: 600;
      color: #666;
      white-space: nowrap;

      i {
        margin-right: 4px;
        color: #195bff;
      }
    }

    .machine-select {
      min-width: 180px;
    }
  }
}

// Compact spacing for better vertical view
.container {
  .box {
    margin-bottom: 10px;
    padding: 12px 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .box-title {
    margin-bottom: 10px;

    h4 {
      margin-bottom: 0;
      font-size: 18px;
      align-items: baseline;

      i {
        margin-right: 8px;
        color: #195bff;
      }

      .countTotal {
        font-size: 14px;
        color: #666;
        font-weight: normal;
      }
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 10px;

  .section-title {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    .header-actions {
      width: 100%;

      .vs-button {
        flex: 1;
      }
    }
  }
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #195bff;
  margin-bottom: 0;

  i {
    margin-right: 6px;
  }
}

// Stats Section
.stats-section {
  margin-bottom: 10px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 10px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  border: 1px solid #eee;

  .stat-icon {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    margin-right: 10px;

    i {
      font-size: 18px;
      color: white;
    }

    &.total {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
    &.branches {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }
    &.active {
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
    }
    &.inactive {
      background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
    }
  }

  .stat-info {
    .stat-value {
      display: block;
      font-size: 18px;
      font-weight: 700;
      color: #333;
    }

    .stat-label {
      display: block;
      font-size: 11px;
      color: #666;
    }
  }
}

// Route Summary Cards
.route-summary-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.route-summary-card {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
    border-color: #195bff;
  }

  &.active {
    background: #e3f2fd;
    border-color: #195bff;
  }

  .route-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #195bff;
    border-radius: 6px;
    margin-right: 10px;

    i {
      color: white;
      font-size: 14px;
    }
  }

  .route-info {
    .route-name {
      display: block;
      font-weight: 600;
      font-size: 13px;
      color: #333;
    }

    .route-stats {
      display: block;
      font-size: 11px;
      color: #666;
    }
  }
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 8px;

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .filter-label {
      font-size: 11px;
      font-weight: 600;
      color: #666;
    }

    .filter-select {
      min-width: 160px;
    }
  }

  .search-input-wrapper {
    position: relative;

    .search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      font-size: 12px;
    }

    .search-input {
      width: 180px;
      padding: 7px 10px 10px 30px;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      font-size: 12px;
      outline: none;
      font-family: "Open Sans", "BoonBaan", "Noto Sans Lao", sans-serif;

      &:focus {
        border-color: #195bff;
      }

      &.full-width {
        width: 100%;
      }
    }
  }

  .status-select {
    padding: 8px 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 12px;
    outline: none;
    min-width: 110px;

    &:focus {
      border-color: #195bff;
    }
  }

  .filter-actions {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    padding-top: 15px;

    ::v-deep .vs-button {
      margin: 0 !important;
      min-height: 36px;
    }
  }

  @media (max-width: 768px) {
    .filter-group {
      width: 100%;

      .search-input-wrapper .search-input,
      .filter-select,
      .status-select {
        width: 100%;
      }
    }

    .filter-actions {
      width: 100%;

      .vs-button {
        flex: 1;
      }
    }
  }
}

.table-container {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8f9fa;

    th {
      padding: 12px;
      text-align: left;
      font-weight: 600;
      font-size: 13px;
      color: #333;
      border-bottom: 2px solid #e0e0e0;
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f0f0f0;
      transition: background 0.2s;

      &:hover {
        background: #fafafa;
      }
    }

    td {
      padding: 12px;
      font-size: 13px;
      color: #333;
    }
  }

  .col-no {
    width: 60px;
    text-align: center;
    color: #999;
  }

  .col-branch {
    .branch-info {
      .branch-name {
        display: block;
        font-weight: 600;
        color: #333;
      }
      .branch-code {
        font-size: 11px;
        color: #666;
      }
    }
  }

  .col-location {
    .location-info {
      .province-name {
        display: block;
        font-weight: 500;
        color: #333;
      }
    }
  }

  .route-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    background: #e8f5e9;
    color: #2e7d32;
    border-radius: 15px;
    font-size: 12px;
    font-weight: 500;

    i {
      font-size: 11px;
    }
  }

  .machine-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: #e3f2fd;
    color: #1565c0;
    border-radius: 15px;
    font-size: 12px;

    i {
      font-size: 11px;
    }
  }

  .global-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: #f5f5f5;
    color: #666;
    border-radius: 15px;
    font-size: 12px;

    i {
      font-size: 11px;
    }
  }

  .status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 15px;
    font-size: 11px;
    font-weight: 600;

    &.active {
      background: #e8f5e9;
      color: #2e7d32;
    }
    &.inactive {
      background: #ffebee;
      color: #c62828;
    }
  }

  .col-created {
    .created-info {
      font-size: 12px;
      color: #333;
    }
    .text-muted {
      color: #999;
    }
  }

  .action-buttons {
    display: flex;
    gap: 5px;
  }
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #999;

  i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #ddd;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 20px;
  border-top: 1px solid #f0f0f0;
}

// Dialog styles
.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;

  i {
    margin-right: 8px;
    color: #195bff;
  }

  &.danger i {
    color: #c62828;
  }
}

.dialog-content {
  padding: 15px 0;
}

// Horizontal form layout
.form-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  .form-label {
    flex-shrink: 0;
    width: 140px;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    padding-top: 8px;
    text-align: right;
    padding-right: 12px;

    &.required::after {
      content: " *";
      color: #c62828;
    }
  }

  .form-control {
    flex: 1;
    min-width: 0;

    .form-hint {
      display: block;
      font-size: 11px;
      color: #999;
      margin-top: 4px;
    }

    .text-danger {
      display: block;
      font-size: 11px;
      color: #c62828;
      margin-top: 4px;
    }
  }

  @media (max-width: 576px) {
    flex-direction: column;

    .form-label {
      width: 100%;
      text-align: left;
      padding-right: 0;
      padding-top: 0;
      margin-bottom: 6px;
    }

    .form-control {
      width: 100%;
    }
  }
}

// Branch selection list for bulk add
.branch-selection-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
}

.branch-selection-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f5f5f5;
  }

  &.selected {
    background: #e3f2fd;

    .branch-checkbox i {
      color: #195bff;
    }
  }

  .branch-checkbox {
    margin-right: 10px;

    i {
      font-size: 18px;
      color: #ccc;
    }
  }

  .branch-details {
    flex: 1;

    .branch-name {
      font-weight: 600;
      color: #333;
      margin-right: 8px;
    }

    .branch-code {
      font-size: 12px;
      color: #666;
      margin-right: 5px;
    }

    .branch-location {
      font-size: 12px;
      color: #999;
    }
  }
}

.empty-branches,
.loading-branches {
  padding: 30px;
  text-align: center;
  color: #999;

  i {
    margin-right: 8px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

// Global search input wrapper (for dialogs)
.form-control {
  .search-input-wrapper {
    position: relative;
    width: 100%;

    .search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      font-size: 12px;
      z-index: 1;
    }

    .search-input {
      width: 100%;
      padding: 10px 10px 10px 32px;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      font-size: 13px;
      outline: none;
      font-family: "Open Sans", "BoonBaan", "Noto Sans Lao", sans-serif;
      box-sizing: border-box;

      &:focus {
        border-color: #195bff;
        box-shadow: 0 0 0 2px rgba(25, 91, 255, 0.1);
      }

      &::placeholder {
        color: #aaa;
      }
    }
  }
}

// Deep styles for model-list-select
::v-deep .ui.search.dropdown {
  min-height: 36px !important;
  width: 100% !important;
  border-radius: 6px !important;
  font-size: 12px !important;

  > .text {
    font-size: 12px !important;
  }

  input.search {
    padding: 6px 10px !important;
    font-size: 12px !important;
  }

  .menu {
    font-size: 12px !important;
    z-index: 100 !important;

    > .item {
      padding: 8px 10px !important;
      font-size: 12px !important;
    }
  }
}
</style>
